import { ComponentInternalInstance, getCurrentInstance, reactive, ref, Ref, computed } from '@vue/composition-api';
import _ from 'lodash';

import CustomDialog from '@/renderer/scripts/utils/CustomDialog';
import { i18n_t } from '@/locales/i18n';
import smh, { SMH } from '@/renderer/scripts/smh';
import tips from '@/renderer/scripts/utils/tips';
import store from '@/renderer/store';
import { checkRoleIsAdmin } from '@/renderer/scripts/utils/UserRole';

export default function handler(params: {
  type?: Ref<string>;
  defaultRoleId?: Ref<number | undefined>;
  allowInactiveUser?: Ref<boolean>;
  originAddedMembers: Ref<CoFile.TeamOrMemberItem[]>;
  getTeamMemberTree(params: { teamId: number | string }): Promise<SMH.TeamMemberItemData[]>;
}) {
  const vm = (getCurrentInstance() as ComponentInternalInstance).proxy;

  const allowInactiveUser = params?.allowInactiveUser || ref(false);
  const originAddedMembers = params?.originAddedMembers || ref([]);
  const currType = params?.type || ref('');
  const defaultRoleId = params?.defaultRoleId;

  // 已添加的用户
  const addedMembers = ref<CoFile.TeamOrMemberItem[]>([]);
  const originAddedMembersSet = new Set<number | string>([]);
  const addedMembersSet = new Set<number | string>([]);

  const roleLoading = ref(false);

  // 控制选择框
  const checkedControl = computed(() => {
    const permissionList = addedMembers.value;
    const checkedtotalList = permissionList.filter(item => item.checked);

    // 选中项
    const number = checkedtotalList.length;

    // 是否全部选中
    const checkAll = number > 0 && number === permissionList.filter(item => !item.disabledOperate).length;

    return {
      number,
      checkAll,
    };
  });

  // 控制全选
  const checkAllChange = reactive({
    checked: false,
    t: Date.now(),
  });

  const methods = {
    initRoleList() {
      roleLoading.value = true;
      // 每次初始化成员权限列表时都重新拉取一次全局权限列表
      store
        .dispatch('GetRoles')
        .then(data => {
          roleLoading.value = false;
        })
        .catch(() => {
          roleLoading.value = false;
        });
    },
    initOriginAdded() {
      const ownerRoleId = store.getters.ownerRole?.id;
      // 先清空 再更新
      methods.resetData();
      addedMembers.value = _.cloneDeep(originAddedMembers.value)
        .map(item => {
          let type = 'authorize';
          if (item.isForbidden) {
            type = 'forbidden';
          } else if (item.tag === 1 || item.tag === 2 || (item.tag === 0 && item.inheritRoleId)) {
            type = 'inherit';
          }
          const itemType = item.userId ? 'user' : 'team';
          const uid = `${itemType}_${item.userId || item.id}`;
          // 数据项可能重复 使用renderKey标记渲染组件 保证唯一
          const renderKey = `${uid}_${Date.now()}`;
          addedMembersSet.add(uid);
          originAddedMembersSet.add(uid);
          return {
            ...item,
            type,
            uid,
            renderKey,
          } as CoFile.TeamOrMemberItem;
        })
        .sort((a, b) => {
          // 所有者权限排最前面
          if (a.roleId !== ownerRoleId && b.roleId === ownerRoleId) return 1;
          // 团队排在用户前面
          if (a.spaceId) {
            if (a.spaceId && !b.spaceId) return -1;
          }
          // 默认权限成员默认排在前面
          if (a.isDefault && a.tag === 1) return -1;
          // 带标签权限排在不带标签之前
          if (a.tag !== 0 && b.tag === 0) return -1;
          return 0;
        });
    },
    addTeamOrMember(item: SMH.TeamTreeNode | SMH.UserBaseInfo, noDisableRoleSelect = true) {
      const currUserId = vm.$store.getters.userId;
      // 不允许自己添加自己
      const user = item as SMH.UserBaseInfo & { userId: number };
      if (user.userId && user.userId === currUserId) {
        return;
      }
      // FIX: 开启了禁用管理员选择角色
      if (
        !noDisableRoleSelect &&
        checkRoleIsAdmin((item as SMH.UserBaseInfo).role) &&
        vm.$route.name !== 'teamManagement'
      )
        return;

      // 未激活用户是否允许操作
      if (item.inactive && !allowInactiveUser.value) return;

      methods.addPermission(item);
    },
    addPermission(item: SMH.TeamTreeNode | SMH.UserBaseInfo) {
      const newAddList = addedMembers.value.filter(item => item.newAdd);
      if (newAddList.length >= 1000) {
        tips.error(i18n_t('一次最多可选择1000人，已到达上限'));
        return;
      }
      const permisionItem: CoFile.TeamOrMemberItem = {
        roleId: i18n_t('设置权限'),
        roleName: '',
        role: '',
        name: '',
        checked: false,
        tag: 0,
        avatar: '',
        inactive: item.inactive,
        isTemporary: !!item.isTemporary,
        uid: '',
      };
      if ((item as SMH.TeamTreeNode).spaceId) {
        const teamItem = item as SMH.TeamTreeNode;
        const uid = `team_${teamItem.id}`;
        permisionItem.spaceId = teamItem.spaceId;
        permisionItem.name = teamItem.name;
        permisionItem.id = teamItem.id;
        permisionItem.userCount = teamItem.userCount;
        permisionItem.uid = uid;
        permisionItem.renderKey = `${uid}_${Date.now()}`;
      } else {
        const userItem = item as SMH.UserBaseInfo & { name: string; userId: number };
        const uid = `user_${userItem.userId}`;
        permisionItem.userId = userItem.userId;
        permisionItem.name = userItem.name;
        permisionItem.countryCode = userItem.countryCode;
        permisionItem.phoneNumber = userItem.phoneNumber;
        permisionItem.accountName = userItem.accountName;
        permisionItem.role = userItem.role;
        permisionItem.avatar = userItem.avatar;
        permisionItem.uid = uid;
        permisionItem.renderKey = `${uid}_${Date.now()}`;
      }
      const errMsg = i18n_t('该团队或成员已添加至列表');
      // 团队管理新增成员时权限使用该团队的默认权限
      if (currType.value.includes('addUser')) {
        permisionItem.roleId = +(defaultRoleId as Ref<number>).value;
      }
      const { editionFlag } = store.getters;
      // 管理员默认操作者权限
      // add: 团队空间中比较特别，不存在操作者，所以跳过，不做任何特殊处理。
      if (checkRoleIsAdmin((item as SMH.UserBaseInfo).role, true) && currType.value !== 'addUserInCoGroup') {
        // 团队版的管理员 默认最高[编辑者]
        // 企业版的管理员 默认最高[操作者]
        const operatorRoleId = store.getters?.operatorRole?.id; // 操作者
        const editorRoleId = store.getters?.editorRole?.id; // 编辑者
        permisionItem.roleId = editionFlag === 'team' ? editorRoleId : operatorRoleId;
      }
      // 授权列表内有相同项则不允许添加
      const index = addedMembers.value.findIndex(item => {
        const isSame =
          (item.spaceId && item.spaceId === permisionItem.spaceId) ||
          (item.userId && item.userId === permisionItem.userId);
        return isSame;
      });

      if (index > -1) {
        tips.error(errMsg);
        return;
      }
      permisionItem.newAdd = true;
      permisionItem.type = 'authorize';
      addedMembers.value.push(permisionItem);
      permisionItem.uid && addedMembersSet.add(permisionItem.uid);
      tips.info(i18n_t('已添加至成员列表'));
    },
    removeConfirm(fn: Function) {
      const dialog = CustomDialog.confirm({
        header: i18n_t('提示'),
        body: i18n_t('确定要删除吗？'),
        theme: 'warning',
        onConfirm: () => {
          if (dialog.hide) {
            dialog.hide();
          }
          fn();
        },
      });
    },
    /**
     * userId可能重复，按照调用removeTeamOrMember根据index删除
     */
    removeTeamOrMember(index: number, member: CoFile.TeamOrMemberItem) {
      // 统一按index删 因为userId可能重复
      const [item] = addedMembers.value.splice(index, 1);
      if (item.uid) {
        addedMembersSet.delete(item.uid);
      }
    },
    removeTeamOrMembers() {
      // 批量移除时需要从后往前移除，否则 index 序号不正确
      for (let i = addedMembers.value.length - 1; i >= 0; i--) {
        const item = addedMembers.value[i];
        item.checked && methods.removeTeamOrMember(i, item);
      }
    },
    changeTeamOrMembersPermission(roleId: string, noDisableRoleSelect = true) {
      if (roleId) {
        addedMembers.value.forEach(item => {
          const isUser = !checkRoleIsAdmin(item.role as string);
          if (item.checked && (isUser || noDisableRoleSelect)) {
            item.roleId = roleId;
          }
        });
      }
    },
    resetData() {
      addedMembers.value = [];
      addedMembersSet.clear();
      originAddedMembersSet.clear();
    },
    /**
     * @description 过滤已改变的权限列表及被删除的权限列表
     * @return {,changed,deleted}
     */
    filterPermisionList() {
      const changed: CoFile.TeamOrMemberItem[] = []; // 改变的授权（包含新增）
      const deleted: CoFile.TeamOrMemberItem[] = []; // 删除的授权
      const added: CoFile.TeamOrMemberItem[] = []; // 新增的授权

      // FIX: 由于自己无法对自己授权，因此授权时要将本人的权限过滤掉
      const noAccessRoleId = vm.$store.getters.roleWithNoAccess?.id || 3880;
      const currUserId = vm.$store.getters.userId;
      const originPermissionList = originAddedMembers.value.filter(item => item.userId !== currUserId);
      const currPermissionList = addedMembers.value.filter(item => item.userId !== currUserId);

      // 过滤出已被删除的继承权限和共享权限
      originPermissionList.forEach(currItem => {
        const currItemId = currItem.spaceId || currItem.userId;
        const index = currPermissionList.findIndex(item => {
          const itemId = item.spaceId || item.userId;
          return currItemId === itemId;
        });
        if (index === -1) {
          const isInherit = currItem.tag === 1 || currItem.tag === 2 || (currItem.tag === 0 && currItem.inheritRoleId);
          if (isInherit) {
            // 从删除列表里取出被删除的继承权限 对其权限改为禁止访问者
            currItem.roleId = noAccessRoleId;
            changed.push(currItem);
          } else {
            // 删除共享权限
            deleted.push(currItem);
          }
        }
      });

      // 过滤出被修改（包含新增）的权限）
      currPermissionList.forEach(currItem => {
        const currItemId = currItem.spaceId || currItem.userId;
        const index = originPermissionList.findIndex(item => {
          const itemId = item.spaceId || item.userId;
          return currItemId === itemId;
        });
        if (index > -1) {
          const item = originPermissionList[index];
          item.roleId !== currItem.roleId && changed.push(currItem);
        } else {
          added.push(currItem);
        }
      });
      return {
        changed,
        deleted,
        added,
      };
    },
  };

  return {
    roleLoading,
    addedMembers,
    addedMembersSet,
    originAddedMembersSet,
    checkedControl,
    checkAllChange,
    methods,
  };
}
